import React, {
  useCallback,
  useContext,
  useEffect, useMemo,
  useRef,
  useState,
} from 'react';
import style from './style';
import Header from './Header';
import {
  getCalculationResult,
  getContextInstance,
  getContextInstanceThirdParty, getContextInstanceThirdPartyAttributes,
} from '../../../services/edge/ContexteService';
import { Context } from '../../../states/Store';
import i18next from 'i18next';
import EAAccordion from '../../../components/EAAccordion';
import FicheContextContent from '../../../components/context/FicheContext/Content';
import FicheContextMenu from '../../../components/context/FicheContext/Menu';
import { FormProvider, useForm } from 'react-hook-form';
import Visualisation from '../ContextDetail/Visualisation';
import Informations from './Informations';
import { CalculationNature } from '../../../services/common/CalculatorUtils';
import CalculatedAttributes from './CalculatedAttributes';
import AttributsBlock from './AttributsBlock';
import DeclarationTiersBlock from './DeclarationTiersBlock';
import Dossiers from './Dossiers';
import Archives from './Archives';
import { getDossiersByCriteria } from '../../../services/edge/DossierService';
import AddingButton from '../../../components/thirdparties/AddingButton';
import {
  PrivilegeEnum,
  RoleBasedAccessControlContext,
} from '../../../services/common/RolesUtils';
import Notes from './Notes';
import { Grid, Tooltip } from '@mui/material';
import { getLatestUpdateDateFormatted } from '../ContextDetail';
import ModificationDate from '../../../components/context/ModificationDate';
import CreatedBy from '../../../components/context/CreatedBy';
import EAButton from '../../../components/common/EAButton';
import ROUTES from '../../../config/Routes';
import EAIcon from '../../../components/common/EAIcon';
import { useHistory } from 'react-router';

const navigationItems = [
  {
    id: 'attributs',
    icon: 'context/navigation/attributs',
    label: 'context.contextDetail.navigation.attributs',
  },
  {
    id: 'visualisation',
    icon: 'context/navigation/visualisation',
    label: 'context.contextDetail.navigation.visualisation',
  },
  {
    id: 'declarationTiers',
    icon: 'context/navigation/tiers',
    label: 'context.contextDetail.navigation.tiers',
  },
  {
    id: 'dossiers',
    icon: 'context/navigation/dossiers',
    label: 'context.contextDetail.navigation.dossiers',
  },
  {
    id: 'dossiersArchives',
    icon: 'context/navigation/dossiers',
    label: 'context.contextDetail.navigation.archives',
  },
  {
    id: 'notes',
    icon: 'context/navigation/notes',
    label: 'context.contextDetail.navigation.notes',
  },
];

const ContextTiers = ({ contextInstanceId, thirdPartyId,selectedContext, setSelectedContext }) => {
  const { classes } = style();
  const navigationRef = useRef();
  const [state, dispatch] = useContext(Context);
  const history = useHistory();
  const associatedTP = state.contextThirdParties;
  const [selectedTp, setSelectedTp] = useState();
  const [calculatedAttributes, setCalculatedAttributes] = useState(null);
  const [indicators, setIndicators] = useState(null);
  const [addDossierPopup, setAddDossierPopup] = useState(false);
  const [activeDossiers, setActiveDossiers] = useState([]);
  const [archivedDossiers, setArchivedDossiers] = useState([]);
  const { hasAnyOfPrivileges } = useContext(RoleBasedAccessControlContext);

  const fetchDossiers = useCallback(
    async ({ monitored = true }) => {
      let searchCriteria = `page=0&pageSize=9999&withAttributes=true&monitored=${monitored}&thirdpartyId=${selectedTp.compteFoId}&ctxThirdparty=${selectedTp.id}`;
      const results = await getDossiersByCriteria(
        state.account.id,
        searchCriteria,
        null
      );
      let resultsOrdered = results.content.sort(
        (a, b) => new Date(b.creationDate) - new Date(a.creationDate)
      );
      return resultsOrdered;
    },
    [selectedTp, state.account.id]
  );

  const populateDossiersData = useCallback(async () => {
    if (!selectedTp) return;
    const activeDossiers = await fetchDossiers({ monitored: true });
    const archivedDossiers = await fetchDossiers({ monitored: false });
    setActiveDossiers(activeDossiers);
    setArchivedDossiers(archivedDossiers);
  }, [selectedTp, fetchDossiers]);

  useEffect(() => {
    populateDossiersData();
  }, [selectedTp, populateDossiersData]);

  const formMethods = useForm({
    mode: 'onChange',
    delayError: 500,
    defaultValues: {
      contextAttributes: [],
      tiersAttributes: [],
      demandeAttributes: [],
    },
  });

  const { reset, getValues } = formMethods;

  const fetchContextInstance = useCallback(async () => {
    try {
      const instance = await getContextInstance(
        state.account.id,
        Number(contextInstanceId)
      );
      setSelectedContext(instance);
    } catch (e) {
      setSelectedContext(null);
    }
  }, [state.account, contextInstanceId,setSelectedContext]);

  const fetchThirdPartyCalculationResults = useCallback(async () => {
    try {
      const res = await getCalculationResult(
        state.account.id,
        thirdPartyId,
        dispatch
      );
      const calculatedAttributes = res.content.filter(
        (r) => r.nature === CalculationNature.CALCULATED_ATTRIBUTE
      );
      const indicators = res.content.filter(
        (r) => r.nature === CalculationNature.INDICATOR
      );
      setIndicators(indicators);
      setCalculatedAttributes(calculatedAttributes);
    } catch (e) {
      setCalculatedAttributes(null);
      setIndicators(null);
    }
  }, [state.account, thirdPartyId, dispatch]);

  useEffect(() => {
    fetchContextInstance();
    fetchThirdPartyCalculationResults();
  }, [fetchContextInstance, fetchThirdPartyCalculationResults]);

  useEffect(() => {
    return () => {
      dispatch({
        type: 'SELECTED_CONTEXT_THIRDPARTY',
        contextThirdParty: null,
      });
    };
  }, [dispatch]);

  const fetchAssociatedTP = useCallback(async () => {
    if (selectedContext) {
      try {
        const res = await getContextInstanceThirdParty(
          state.account.id,
          selectedContext.id,
          dispatch
        );
        const selectedTp = res.find((r) => r.id === thirdPartyId);
        setSelectedTp(selectedTp);
        const tpAttributes = await getContextInstanceThirdPartyAttributes(
          state.account.id,
          selectedTp.id,
          selectedContext.id,
        )

        dispatch({
          type: 'SELECTED_CONTEXT_THIRDPARTY',
          contextThirdParty: selectedTp,
        });
        reset({
          ...selectedTp,
          tiersAttributes: tpAttributes.tiersAttributes,
          demandeAttributes: tpAttributes.demandeAttributes,
          contextAttributes: selectedContext.contextAttributes,
        });
      } catch (e) {
        console.log(e);
      }
    }
  }, [selectedContext, dispatch, reset, state.account.id, thirdPartyId]);

  useEffect(() => {
    fetchAssociatedTP();
  }, [fetchAssociatedTP]);

  const onEditAttribute = useCallback(() => {
    fetchAssociatedTP();
  }, [fetchAssociatedTP]);

  const calculatedAttributesUpdateDate = getLatestUpdateDateFormatted(
    calculatedAttributes,
    'resultLastUpdateDate'
  );
  const indicatorsUpdateDate = getLatestUpdateDateFormatted(
    indicators,
    'resultLastUpdateDate'
  );

  const shouldDisplayAttributesBlock =
    (hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT_ATTRIBUTES) ||
      hasAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT_THIRD_PARTY_ATTRIBUTES
      )) &&
    (getValues('contextAttributes').length ||
      getValues('tiersAttributes').length);


  const filteredNavigationItems = useMemo(() => {
    return navigationItems.filter((item) => {
      if (item.id === 'attributs') return shouldDisplayAttributesBlock;
      if (item.id === 'visualisation') return associatedTP.length > 0;
      return true;
    });
  }, [shouldDisplayAttributesBlock, associatedTP.length]);

  return (
    <>
      <Header
        context={selectedContext}
        selectedTp={selectedTp}
        fetchAssociatedTP={() => fetchAssociatedTP(selectedContext)}
      />
      {selectedContext && selectedTp ? (
        <FicheContextContent ref={navigationRef}>
          {selectedTp && (
            <CreatedBy
              author={selectedTp.createdBy}
              creationDate={selectedTp.creationDate}
            />
          )}
          <Informations tp={selectedTp} context={selectedContext} />
          <FicheContextMenu
            navigationItems={filteredNavigationItems}
            ref={navigationRef}
            additionalClass={classes.menu}
          />
          <FormProvider {...formMethods}>
            <Grid container spacing={2} id={'attributs'}>
              {shouldDisplayAttributesBlock ? (
                <Grid item xs={12}>
                  <AttributsBlock
                    onEditAttribute={onEditAttribute}
                    contextInstanceId={contextInstanceId}
                    selectedTp={selectedTp}
                  />
                </Grid>
              ) : (
                <></>
              )}
              {indicators && indicators.length ? (
                <Grid item xs={12}>
                  <EAAccordion
                    id={'indicateurs'}
                    title={i18next.t(
                      `context.contextDetail.blocks.indicateurs`
                    )}
                    logo="/context/calculated_attribute_block"
                    titleNoResult={true}
                    opened={true}
                    buttons={
                      indicatorsUpdateDate
                        ? [<ModificationDate date={indicatorsUpdateDate} />]
                        : []
                    }
                  >
                    <CalculatedAttributes
                      attributes={indicators}
                      type={'indicator'}
                    />
                  </EAAccordion>
                </Grid>
              ) : (
                <></>
              )}
              {calculatedAttributes && calculatedAttributes.length ? (
                <Grid item xs={12}>
                  <EAAccordion
                    id={'calculated_attributes'}
                    title={i18next.t(
                      `context.contextDetail.blocks.calculatedAttributes`
                    )}
                    logo="/context/attributs_block"
                    titleNoResult={true}
                    opened={true}
                    buttons={
                      calculatedAttributesUpdateDate
                        ? [
                            <ModificationDate
                              date={calculatedAttributesUpdateDate}
                            />,
                          ]
                        : []
                    }
                  >
                    <CalculatedAttributes
                      attributes={calculatedAttributes}
                      type={'attribute'}
                    />
                  </EAAccordion>
                </Grid>
              ) : (
                <></>
              )}

              <Grid item xs={12} id={'visualisation'}>
                <EAAccordion
                  logo={'/context/visualisation_block'}
                  title={i18next.t(
                    `context.contextDetail.blocks.visualisation`
                  )}
                  titleNoResult={true}
                  opened={true}
                >
                  <Visualisation
                    context={selectedContext}
                    associatedTP={associatedTP}
                    selectedTP={thirdPartyId}
                    fetchContextInstance={fetchContextInstance}
                    fetchAssociatedTP={fetchAssociatedTP}
                  />
                </EAAccordion>
              </Grid>
              <Grid item xs={12} id={'declarationTiers'}>
                <EAAccordion
                  logo={'/context/visualisation_block'}
                  title={
                    <>
                      <span>
                        {i18next.t(
                          `context.contextDetail.blocks.tiers`
                        )}
                      </span>
                      <EAButton
                        onClick={() => {
                          history.push(
                            `${ROUTES.CONTEXT_DETAIL_ADD_TP}?instance=${selectedContext.id}&start=${thirdPartyId}`
                          );
                        }}
                        content={
                          <>
                            <EAIcon icon={'context/addAssocier'} />
                            <span style={{ marginLeft: '5px' }}>
                              {i18next.t('context.addTiers.add')}
                            </span>
                          </>
                        }
                        customStyle={{
                          position: 'absolute',
                          right: 50,
                          marginLeft: 'auto',
                        }}
                        disabled={
                          selectedTp.isArchived ||
                          !hasAnyOfPrivileges(
                            PrivilegeEnum.PRIVILEGE_ADD_THIRDPARTY_CONTEXT
                          )
                        }
                      />
                    </>
                  }
                  titleNoResult={true}
                  opened={true}
                >
                  <DeclarationTiersBlock
                    onEditAttribute={onEditAttribute}
                    thirdPartyId={thirdPartyId}
                    associatedTP={associatedTP}
                    context={selectedContext}
                  />
                </EAAccordion>
              </Grid>
              <Grid item xs={12} id={'dossiers'}>
                <EAAccordion
                  logo={'/context/dossiers'}
                  title={i18next.t(`context.contextDetail.blocks.dossiers`)}
                  titleNoResult={true}
                  opened={true}
                  buttons={[
                    <Tooltip title={i18next.t('dossierTable.addBtnLabel')}>
                      <div>
                        <AddingButton
                          label={i18next.t('dossierTable.addBtnLabel')}
                          handleClick={() => {
                            setAddDossierPopup(true);
                          }}
                          disabled={selectedTp.isArchived}
                        />
                      </div>
                    </Tooltip>,
                  ]}
                >
                  <Dossiers
                    addDossierPopup={addDossierPopup}
                    setAddDossierPopup={setAddDossierPopup}
                    contextThirdparty={selectedTp}
                    activeDossiers={activeDossiers}
                    refetch={populateDossiersData}
                  />
                </EAAccordion>
              </Grid>
              <Grid item xs={12} id={'dossiersArchives'}>
                <EAAccordion
                  logo="archive"
                  title={i18next.t('archives')}
                  titleNoResult={true}
                  opened={true}
                >
                  <Archives
                    contextThirdparty={selectedTp}
                    archivedDossiers={archivedDossiers}
                    refetch={populateDossiersData}
                  />
                </EAAccordion>
              </Grid>
              <Grid item xs={12} id={'notes'}>
                <Notes contexThirdParty={selectedTp} />
              </Grid>
            </Grid>
          </FormProvider>
        </FicheContextContent>
      ) : (
        <></>
      )}
    </>
  );
};

export default ContextTiers;
